import { keepPreviousData, useQuery } from "@tanstack/react-query"

import {
  v1CustomerPaymentMetrix,
  type V1CustomerPaymentMatrixResponse,
} from "@be-miniapp/api"

export const paymentMethodsQueryKeys = {
  all: ["payment-methods"],
  single: (service: string, paymentMethods: number[] = []) => [
    ...paymentMethodsQueryKeys.all,
    service,
    paymentMethods,
  ],
}

interface UsePaymentMethodsParams {
  service: string
  paymentMethods?: number[]
  source?: number
  refreshInterval?: number | false
  staleTime?: number
  refetchIntervalInBackground?: boolean
}

const EMPTY_PAYMENT_MATRIX_RESPONSE = {
  flag: 0,
  data: [],
} satisfies V1CustomerPaymentMatrixResponse

function getResponseData(value: unknown) {
  if (!value || typeof value !== "object") return undefined
  return (value as { data?: unknown }).data
}

function isPaymentMatrixResponse(
  value: unknown
): value is V1CustomerPaymentMatrixResponse {
  return (
    !!value &&
    typeof value === "object" &&
    Array.isArray(getResponseData(value))
  )
}

function normalizePaymentMatrixResponse(
  value: unknown
): V1CustomerPaymentMatrixResponse {
  if (isPaymentMatrixResponse(value)) return value

  const nestedValue = getResponseData(value)
  if (isPaymentMatrixResponse(nestedValue)) return nestedValue

  const doubleNestedValue = getResponseData(nestedValue)
  if (isPaymentMatrixResponse(doubleNestedValue)) return doubleNestedValue

  return EMPTY_PAYMENT_MATRIX_RESPONSE
}

function usePaymentMethods({
  service,
  paymentMethods = [],
  source,
  refreshInterval,
  staleTime,
  refetchIntervalInBackground,
}: UsePaymentMethodsParams) {
  return useQuery({
    queryKey: paymentMethodsQueryKeys.single(service, paymentMethods),
    queryFn: async () => {
      const response = await v1CustomerPaymentMetrix({
        service,
        type: 1,
        ...(source ? { source } : {}),
        ...(paymentMethods.length > 0
          ? { payment_methods: paymentMethods }
          : {}),
      })
      return normalizePaymentMatrixResponse(response)
    },
    enabled: true,
    refetchOnWindowFocus: true,
    ...(refreshInterval !== undefined
      ? { refetchInterval: refreshInterval }
      : {}),
    ...(staleTime !== undefined ? { staleTime } : {}),
    ...(refetchIntervalInBackground !== undefined
      ? { refetchIntervalInBackground }
      : {}),
    placeholderData: keepPreviousData,
  })
}

export { usePaymentMethods }
export type { UsePaymentMethodsParams }
